import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MessageCircle, Coins, Loader2, Building2, Plus } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const PLAN_LABEL = { essenziale: "Essenziale", premium: "Premium", elite: "Elite", "": "Nessun piano" };

// Sezione "Crediti SMS" (inserita nella pagina Sedio Partner per ridurre le pagine).
export function SmsCreditiSection() {
  const [wallet, setWallet] = useState(null);
  const [usage, setUsage] = useState(null);
  const [buying, setBuying] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirmPkg, setConfirmPkg] = useState(null);

  const load = async () => {
    try {
      const [w, u] = await Promise.all([api.get("/sms/wallet"), api.get("/sms/usage")]);
      setWallet(w.data);
      setUsage(u.data);
    } catch (e) {
      toast.error("Impossibile caricare i crediti SMS");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const buy = async (pkg) => {
    setBuying(pkg.id);
    setConfirmPkg(null);
    try {
      const r = await api.post("/sms/buy", { package_id: pkg.id });
      toast.success(`+${r.data.added} SMS aggiunti · residuo pacchetti: ${r.data.pool_balance}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Acquisto non riuscito");
    } finally { setBuying(""); }
  };

  const packages = wallet?.packages || [];
  const buildings = usage?.buildings || [];

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="crediti-sms-section">
      <div>
        <div className="flex items-center gap-2 mb-1"><MessageCircle className="h-4 w-4 text-emerald-600" /><h3 className="font-head text-lg font-semibold">Crediti SMS</h3></div>
        <p className="text-[12px] text-muted-foreground max-w-2xl">
          Gli SMS servono per l'invio delle credenziali dei portali (piani Premium ed Elite) e per le risposte
          nelle pratiche AI (solo Elite). Ogni condominio ha una quota annua inclusa (Premium 150, Elite 300);
          esaurita la quota si usano i pacchetti SMS acquistati qui in SedioCoin.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-4"><Loader2 className="h-4 w-4 animate-spin" />Caricamento…</div>
      ) : (<>
        {/* Saldo pacchetti */}
        <div className="rounded-lg border border-border bg-secondary/30 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="sms-pool-card">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Pacchetti SMS disponibili (account)</div>
            <div className="text-3xl font-extrabold mt-1 flex items-center gap-2"><MessageCircle className="h-6 w-6 text-emerald-600" />{wallet?.pool_balance ?? 0}</div>
          </div>
          <div className="text-[11px] text-muted-foreground max-w-xs">Usati automaticamente quando la quota annua del condominio è esaurita.</div>
        </div>

        {/* Pacchetti */}
        <div data-testid="sms-packages">
          <div className="font-semibold text-sm mb-2 flex items-center gap-2"><Plus className="h-4 w-4 text-emerald-600" />Acquista un pacchetto SMS <span className="text-[11px] font-normal text-muted-foreground">· saldo {wallet?.coin_balance ?? 0} SedioCoin</span></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {packages.map((pkg) => (
              <div key={pkg.id} className="rounded-lg border border-border p-4 text-center hover:border-emerald-500 transition-colors" data-testid={`sms-pkg-${pkg.id}`}>
                <div className="text-2xl font-extrabold">{pkg.sms}</div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-3">SMS</div>
                <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={buying === pkg.id} data-testid={`sms-buy-${pkg.id}`} onClick={() => setConfirmPkg(pkg)}>
                  {buying === pkg.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Coins className="h-3.5 w-3.5 mr-1.5" />{pkg.coin} coin</>}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Contatore per condominio */}
        <div data-testid="sms-usage-table">
          <div className="font-semibold text-sm mb-2 flex items-center gap-2"><Building2 className="h-4 w-4 text-[#007BFF]" />SMS usati per condominio (anno in corso)</div>
          {buildings.length === 0 ? (
            <div className="text-sm text-muted-foreground">Nessun condominio in gestione.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4">Condominio</th>
                    <th className="py-2 pr-4">Piano</th>
                    <th className="py-2 pr-4">Quota annua</th>
                    <th className="py-2 pr-4">Usati (quota)</th>
                    <th className="py-2 pr-4">Da pacchetti</th>
                    <th className="py-2 pr-4">Totale</th>
                  </tr>
                </thead>
                <tbody>
                  {buildings.map((b) => (
                    <tr key={b.building_id} className="border-b border-border/50" data-testid={`sms-usage-row-${b.building_id}`}>
                      <td className="py-2 pr-4 font-medium">{b.name}</td>
                      <td className="py-2 pr-4">{PLAN_LABEL[b.plan] ?? b.plan}</td>
                      <td className="py-2 pr-4">{b.quota}</td>
                      <td className="py-2 pr-4"><span className={b.quota > 0 && b.quota_used >= b.quota ? "text-red-600 font-semibold" : ""}>{b.quota_used}{b.quota > 0 ? ` / ${b.quota}` : ""}</span></td>
                      <td className="py-2 pr-4">{b.pool_used}</td>
                      <td className="py-2 pr-4 font-semibold" data-testid={`sms-total-${b.building_id}`}>{b.total_used}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {usage?.exempt && <div className="mt-2 text-[11px] text-emerald-700">Account esente: nessun limite SMS applicato (i contatori restano a scopo statistico).</div>}
        </div>
      </>)}

      <AlertDialog open={!!confirmPkg} onOpenChange={(o) => { if (!o) setConfirmPkg(null); }}>
        <AlertDialogContent data-testid="sms-buy-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi l'acquisto?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per acquistare <strong>{confirmPkg?.sms} SMS</strong> per <strong>{confirmPkg?.coin} SedioCoin</strong>.
              L'importo verrà scalato dal tuo saldo (attuale: {wallet?.coin_balance ?? 0} SedioCoin) e gli SMS confluiranno nel pool condiviso del tuo account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sms-buy-confirm-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="sms-buy-confirm-ok" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => buy(confirmPkg)}>
              Conferma acquisto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default SmsCreditiSection;
